/**
 * Frontend i18n configuration for Pakka
 * Supports all major Indian languages
 */

// Supported languages
export const SUPPORTED_LANGUAGES = {
  en: { name: 'English', native: 'English', rtl: false },
  hi: { name: 'Hindi', native: 'हिन्दी', rtl: false },
  bn: { name: 'Bengali', native: 'বাংলা', rtl: false },
  te: { name: 'Telugu', native: 'తెలుగు', rtl: false },
  mr: { name: 'Marathi', native: 'मराठी', rtl: false },
  ta: { name: 'Tamil', native: 'தமிழ்', rtl: false },
  gu: { name: 'Gujarati', native: 'ગુજરાતી', rtl: false },
  kn: { name: 'Kannada', native: 'ಕನ್ನಡ', rtl: false },
  ml: { name: 'Malayalam', native: 'മലയാളം', rtl: false },
  pa: { name: 'Punjabi', native: 'ਪੰਜਾਬੀ', rtl: false },
  or: { name: 'Odia', native: 'ଓଡ଼ିଆ', rtl: false },
  as: { name: 'Assamese', native: 'অসমীয়া', rtl: false },
  ur: { name: 'Urdu', native: 'اردو', rtl: true },
};

export const DEFAULT_LANGUAGE = 'en';

// Get browser language
export const getBrowserLanguage = () => {
  const browserLang = navigator.language.split('-')[0];
  return SUPPORTED_LANGUAGES[browserLang] ? browserLang : DEFAULT_LANGUAGE;
};

// Get stored language preference
export const getStoredLanguage = () => {
  return localStorage.getItem('language') || getBrowserLanguage();
};

// Store language preference
export const setStoredLanguage = (lang) => {
  localStorage.setItem('language', lang);
  // Update HTML lang attribute
  document.documentElement.lang = lang;
  // Update direction for RTL languages
  document.documentElement.dir = SUPPORTED_LANGUAGES[lang]?.rtl ? 'rtl' : 'ltr';
};

// Translation cache
let translationsCache = {};

// Load translations for a language
export const loadTranslations = async (lang) => {
  if (translationsCache[lang]) {
    return translationsCache[lang];
  }

  try {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const response = await fetch(`${backendUrl}/api/translations/${lang}`);
    if (!response.ok) {
      throw new Error(`Failed to load translations for ${lang}`);
    }
    const translations = await response.json();
    translationsCache[lang] = translations;
    return translations;
  } catch (error) {
    console.error(`Error loading translations for ${lang}:`, error);
    // Fallback to English
    if (lang !== DEFAULT_LANGUAGE) {
      return loadTranslations(DEFAULT_LANGUAGE);
    }
    return {};
  }
};

// Get nested value from object using dot notation
const getNestedValue = (obj, path) => {
  if (!path || typeof path !== 'string') return null;
  return path.split('.').reduce((current, key) => current?.[key], obj);
};

// Translate function
export const translate = (key, variables = {}, translations = {}) => {
  let translation = getNestedValue(translations, key);

  if (!translation) {
    if (Object.keys(translations).length > 0) {
      console.warn(`Translation not found: ${key}`);
    }
    return key;
  }

  // Replace variables
  Object.keys(variables).forEach(varKey => {
    translation = translation.replace(`{${varKey}}`, variables[varKey]);
  });

  return translation;
};

// Format currency based on language
export const formatCurrency = (amount, currency, lang) => {
  try {
    const locale = {
      en: 'en-IN',
      hi: 'hi-IN',
      bn: 'bn-IN',
      te: 'te-IN',
      mr: 'mr-IN',
      ta: 'ta-IN',
      gu: 'gu-IN',
      kn: 'kn-IN',
      ml: 'ml-IN',
      pa: 'pa-IN',
      or: 'or-IN',
      as: 'as-IN',
      ur: 'ur-IN',
    }[lang] || 'en-IN';

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency || 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (error) {
    console.error('Error formatting currency:', error);
    return `${currency} ${amount}`;
  }
};

// Format date based on language
export const formatDate = (date, lang, format = 'short') => {
  try {
    const locale = {
      en: 'en-IN',
      hi: 'hi-IN',
      bn: 'bn-IN',
      te: 'te-IN',
      mr: 'mr-IN',
      ta: 'ta-IN',
      gu: 'gu-IN',
      kn: 'kn-IN',
      ml: 'ml-IN',
      pa: 'pa-IN',
      or: 'or-IN',
      as: 'as-IN',
      ur: 'ur-IN',
    }[lang] || 'en-IN';

    const options = format === 'long'
      ? { year: 'numeric', month: 'long', day: 'numeric' }
      : { year: 'numeric', month: 'short', day: 'numeric' };

    return new Intl.DateTimeFormat(locale, options).format(new Date(date));
  } catch (error) {
    console.error('Error formatting date:', error);
    return date;
  }
};

// Format number based on language
export const formatNumber = (number, lang) => {
  try {
    const locale = {
      en: 'en-IN',
      hi: 'hi-IN',
      bn: 'bn-IN',
      te: 'te-IN',
      mr: 'mr-IN',
      ta: 'ta-IN',
      gu: 'gu-IN',
      kn: 'kn-IN',
      ml: 'ml-IN',
      pa: 'pa-IN',
      or: 'or-IN',
      as: 'as-IN',
      ur: 'ur-IN',
    }[lang] || 'en-IN';

    return new Intl.NumberFormat(locale).format(number);
  } catch (error) {
    console.error('Error formatting number:', error);
    return number;
  }
};
